import {ServerResponse} from './types.model';
import {SupportedLanguages} from './language.model';
import {ITenantIntegration} from './integration.model';

export enum UserAccessLevel {
  Administrator = 'Administrator',
  Full = 'Full',
  PortalOnly = 'PortalOnly',
  DownloadOnly = 'DownloadOnly'
}

export function hasAppAccess(level: UserAccessLevel): boolean {
  return level !== UserAccessLevel.DownloadOnly;
}

export const APP_ACCESS_LEVELS: UserAccessLevel[] = [
  UserAccessLevel.Administrator,
  UserAccessLevel.Full,
  UserAccessLevel.PortalOnly
];

export interface IUserData {
  _id: string;
  username?: string;
  tenantName: string;
  tenantEnterpriseNumber: string;
  tenantEmail: string;
  tenantTier: string;
  tenantPhone: string;
  tenantAddress: string;
  tenantCity: string;
  tenantZip: string;
  tenantCountry: string;
  tenantLanguage: SupportedLanguages;
  isActive: boolean;
  tenantLocked: boolean;
  tenantId: string;
  tenantAdminUserName: string;
  tenantSqsQueueUrl: string;
  createdOn: Date;
  tenantLimits: {
    companiesCount: number;
    expirationDate: Date | null;
  };
  penneoInformation: {
    folderId: number;
    createdOn: Date;
  };
  tenantProducts: string[];
  tenantIntegrations: ITenantIntegration[];
  userAccessLevel: UserAccessLevel;
}

export interface ITenantServerResponse extends ServerResponse {}

export interface ITenantUser {
  email: string;
  userName: string;
  userAccessLevel: string;
  enabled: boolean;
  status: string;
  language?: string;
  created: Date;
  allowedCustomers?: string[]; // customer _ids; empty/absent means full access
}

export interface ITenantUsersResponse extends ServerResponse {
  body: ITenantUser[];
}

export interface ICreateTenantUserPayload {
  email: string;
  accessLevel: UserAccessLevel;
  language: SupportedLanguages;
  restrictedAccess: boolean;
  allowedCustomers?: string[]; // customer _ids when access is restricted
}

export interface IUpdateTenantUserPayload {
  userName: string;
  accessLevel: UserAccessLevel;
  language: SupportedLanguages;
  restrictedAccess: boolean;
  allowedCustomers?: string[]; // customer _ids when access is restricted
}

export interface AuthResponse extends ServerResponse {
  user: IUserData;
  token: string;
  expires: number;
}

export const emptyUser: IUserData = {
  _id: '',
  username: '',
  tenantName: '',
  tenantEnterpriseNumber: '',
  tenantEmail: '',
  tenantTier: '',
  tenantPhone: '',
  tenantAddress: '',
  tenantCity: '',
  tenantZip: '',
  tenantCountry: '',
  tenantLanguage: SupportedLanguages.en,
  isActive: false,
  tenantLocked: false,
  tenantId: '',
  tenantAdminUserName: '',
  tenantSqsQueueUrl: '',
  createdOn: new Date(),
  tenantLimits: {
    companiesCount: 0,
    expirationDate: new Date()
  },
  penneoInformation: {
    folderId: 0,
    createdOn: new Date()
  },
  tenantProducts: [],
  tenantIntegrations: [],
  userAccessLevel: UserAccessLevel.DownloadOnly
}

export function convertToIUserData(data: any): IUserData {
  return {
    _id: data._id,
    username: data.username,
    tenantName: data.tenantName,
    tenantEnterpriseNumber: data.tenantEnterpriseNumber,
    tenantEmail: data.tenantEmail,
    tenantTier: data.tenantTier,
    tenantPhone: data.tenantPhone,
    tenantAddress: data.tenantAddress,
    tenantCity: data.tenantCity,
    tenantZip: data.tenantZip,
    tenantCountry: data.tenantCountry,
    tenantLanguage: data.tenantLanguage,
    isActive: data.isActive,
    tenantLocked: data.tenantLocked,
    tenantId: data.tenantId,
    tenantAdminUserName: data.tenantAdminUserName,
    tenantSqsQueueUrl: data.tenantSqsQueueUrl,
    createdOn: new Date(data.createdOn),
    tenantLimits: {
      companiesCount: data.tenantLimits.companiesCount,
      expirationDate: data.tenantLimits.expirationDate ? new Date(data.tenantLimits.expirationDate) : null,
    },
    penneoInformation: {
      folderId: data?.penneoInformation?.folderId || 0,
      createdOn: new Date(data.penneoInformation?.createdOn)
    },
    tenantProducts: data.tenantProducts || [],
    tenantIntegrations: data.tenantIntegrations || [],
    userAccessLevel: data.userAccessLevel || UserAccessLevel.Full
  };
}
